import * as React from 'react';

import { HonestWork } from '@cian/honestwork-component';
import { OwnerBadge } from '@cian/owner-badge-component';
import { plural } from '@cian/utils/lib/shared/plural';
import { mergeStyles } from '@cian/utils/lib/shared/style';

import {
  IAgentReviewsSchema,
  IAgentSchema,
  INewObjectBuilderSchema,
  IOfferSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { Avatar } from '../../common/components/avatar';
import { Link } from '../../common/components/link';
import { LinkWrapper } from '../../common/components/link_wrapper';
import { Tag } from '../../common/components/tag';
import {
  getAgentName,
  getAuthorLabel,
  getVerifyWay,
  isFromBuilder,
  isHomeowner,
  isHomeownerVerified,
  isHonest,
  isOfficialSeller,
  isPro,
  isBroker,
} from '../../utils/agent';
import { getNewbuildingsListQueryStringUrl } from '../../utils/formatters';
import { getOfferType, isCommercial } from '../../utils/offer_type';
import { getExperienceText } from '../../utils/getExperienceText';
import { isFakeSubAgent } from '../../utils/isFakeSubAgent';
import { getAgentBrand } from '../../utils/getAgentBrand';
import { getAgentAvatar } from '../../utils/getAgentAvatar';
import { getFullProfileLink } from '../../utils/getFullProfileLink';
import { createAgentIdName } from '../../utils/createAgentIdName';
import { isManagementCompany } from '../../utils/isManagementCompany';
import { isRentDepartment } from '../../utils/isRentDepartment';
import { isAvatarAgentRoundness } from '../../utils/isAvatarAgentRoundness';
import { OfferContactsAsideContainer } from '../contacts_aside/container';
import { AgentPreviewContainer } from '../agent_preview/container';
import { getCardType, trackGoToCard } from './tracking';
import { AgentStats } from '../agent_stats';
import { PartnerTooltip } from '../PartnerTooltip';

import * as styles from './index.css';

export interface IAuthorAsideBrandProps {
  agent: IAgentSchema | null;
  companyFoundationTime: string | null;
  agentReviews?: IAgentReviewsSchema | null;
  developer: INewObjectBuilderSchema | null;
  offer: IOfferSchema;
  isAgentExperienceExperimentActive: boolean;
  showCountrysideBuilderBadge: boolean;
}

export class AuthorAsideBrand extends React.PureComponent<IAuthorAsideBrandProps> {
  public render() {
    const { agent, agentReviews, developer, offer, companyFoundationTime, showCountrysideBuilderBadge } = this.props;

    const { status } = offer;
    const offerType = getOfferType(offer);

    if (!agent) {
      return null;
    }

    const { isCianPartner, isDeveloper, isSubAgent } = agent;
    const isAgentPublished = agent.status === 'published';

    const isProAgent = isPro(agent);
    const isHonestAgent = isHonest(agent);
    const isBrokerAgent = isBroker(agent);
    const isAgentWithCompanyName = isFakeSubAgent(agent, offer);

    const profileLink = getFullProfileLink(agent, offer, developer);

    const avatarHandler = profileLink ? this.handlerAgentAvatarClick : undefined;
    const nameHandler = profileLink ? this.handleAgentNameClick : undefined;
    const name = isSubAgent ? agent.companyName : getAgentName(agent, offer);
    const experienceText = getExperienceText(agent, offer);
    const isVerified = isHomeownerVerified(offer, agent);
    const agentBrand = getAgentBrand(agent, offer);
    const verifyWay = getVerifyWay(offer, agent);
    const isCommercialOffer = isCommercial(offerType);
    const isFromBuilderOffer = isFromBuilder(offer);
    const isPublishedOffer = status === 'published';
    const agentId = createAgentIdName(agent);
    const avatar = getAgentAvatar(agent);
    const label = getAuthorLabel(offer, showCountrysideBuilderBadge);
    const isStatsVisible =
      isBrokerAgent || isAgentWithCompanyName || isRentDepartment(agent) || isManagementCompany(agent);

    const isAvatarRoudness = isAvatarAgentRoundness(offer, agent);

    return (
      <>
        <div className={styles['container']}>
          <div className={styles['content']}>
            {(isHonestAgent || isProAgent || isCianPartner || label) && (
              <div className={styles['honest-container']}>
                {isHonestAgent && (
                  <HonestWork
                    agentLink={{
                      agentId: agent.id,
                      otherLinkProps: {
                        deal_type: offer.dealType,
                        offer_type: offer.offerType,
                      },
                    }}
                    className={styles['tag']}
                  />
                )}
                {isProAgent && (
                  <Tag {...mergeStyles([styles['tag'], styles['tag-pro']])} title="Профессиональный пользователь">
                    Pro
                  </Tag>
                )}
                {label &&
                  (label === 'homeowner' ? (
                    <OwnerBadge isVerified={isVerified} verifyWay={verifyWay} />
                  ) : (
                    <Tag className={styles['tag']}>{label}</Tag>
                  ))}
                {isCianPartner && (
                  <PartnerTooltip
                    master={<Tag {...mergeStyles([styles['tag'], styles['tag-partner']])}>Партнёр</Tag>}
                  />
                )}
              </div>
            )}
            <div className={styles['link-wrapper']}>
              {isAgentPublished ? (
                <LinkWrapper target="_blank" href={profileLink} onClick={nameHandler} className={styles['title-link']}>
                  <h2 className={styles['title']}>{name || agentId}</h2>
                </LinkWrapper>
              ) : (
                <h2 className={styles['title']}>{name || agentId}</h2>
              )}
            </div>
            {agentBrand && !showCountrysideBuilderBadge && <div className={styles['prop']}>{agentBrand}</div>}
            {isFromBuilder(offer) && companyFoundationTime && (
              <div className={styles['prop']}>Год основания: {companyFoundationTime}</div>
            )}
            {isOfficialSeller(offer) && experienceText && <div className={styles['prop']}>{experienceText}</div>}
            {isFromBuilderOffer && developer && developer.stats && (
              <ul className={styles['company']}>
                {developer.stats.done && developer.stats.done.qs && developer.stats.done.text ? (
                  <li className={styles['company-item']}>
                    <Link
                      href={getNewbuildingsListQueryStringUrl(developer.stats.done.qs)}
                      target="_blank"
                      onClick={this.handleBuildingDoneClick}
                    >
                      Сдано {developer.stats.done.text}
                    </Link>
                  </li>
                ) : (
                  <li className={styles['company-item']}>
                    <span>Нет сданных ЖК</span>
                  </li>
                )}
                {isDeveloper &&
                developer.stats.progress &&
                developer.stats.progress.qs &&
                developer.stats.progress.text ? (
                  <li className={styles['company-item']}>
                    <Link
                      href={getNewbuildingsListQueryStringUrl(developer.stats.progress.qs)}
                      target="_blank"
                      onClick={this.handleBuildingProgressClick}
                    >
                      Строится {developer.stats.progress.text}
                    </Link>
                  </li>
                ) : (
                  <li className={styles['company-item']}>
                    <span>Нет строящихся ЖК</span>
                  </li>
                )}
              </ul>
            )}
            {isStatsVisible && (
              <AgentStats uniqueKey="broker-stats" agentReviews={agentReviews} link={`/agents/${agent.id}`} />
            )}
            {(!isDeveloper || isAgentWithCompanyName) && experienceText && (
              <div className={styles['prop']}>{experienceText}</div>
            )}
            {(!isDeveloper || isAgentWithCompanyName) &&
              !!agent.offersCount &&
              agent.offersCount > 1 &&
              agent.offersLink && (
                <Link
                  target="_blank"
                  href={agent.offersLink}
                  className={styles['offers-link']}
                  onClick={this.handleQtyLinkClick}
                >
                  Ещё {agent.offersCount - 1}&nbsp;
                  {plural(agent.offersCount - 1, ['объект', 'объекта', 'объектов'])}
                </Link>
              )}
            {isFromBuilderOffer && profileLink && (
              <Link
                target="_blank"
                href={profileLink}
                className={styles['offers-link']}
                onClick={this.handleMoreLinkClick}
              >
                Подробнее о застройщике
              </Link>
            )}
            {isPublishedOffer && isCommercialOffer && <OfferContactsAsideContainer />}
          </div>
          <LinkWrapper
            target="_blank"
            notRemoveWrapper
            href={isAgentPublished ? profileLink : undefined}
            onClick={isAgentPublished ? avatarHandler : undefined}
            className={styles['avatar-wrapper']}
          >
            <Avatar
              className={styles['avatar']}
              url={avatar}
              isRoundness={isAvatarRoudness}
              character={(!isHomeowner(offer) && name && name[0]) || ''}
            />
          </LinkWrapper>
        </div>
        {isSubAgent && (
          <div className={styles['preview']}>
            <AgentPreviewContainer onClick={this.handleAgentPreviewClick} />
          </div>
        )}
      </>
    );
  }

  private handleBuildingProgressClick = () => trackGoToCard(getCardType(this.props.offer), 'in_progress_JK');
  private handleBuildingDoneClick = () => trackGoToCard(getCardType(this.props.offer), 'ready_JK');
  private handleQtyLinkClick = () => trackGoToCard(getCardType(this.props.offer), 'Qty');
  private handleMoreLinkClick = () => trackGoToCard(getCardType(this.props.offer), 'more');
  private handleAgentPreviewClick = () => trackGoToCard(getCardType(this.props.offer), 'sub');
  private handlerAgentAvatarClick = () => trackGoToCard(getCardType(this.props.offer), 'ava');
  private handleAgentNameClick = () => trackGoToCard(getCardType(this.props.offer), 'name');
}
